import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Car, 
  TrendingUp, 
  TrendingDown, 
  Calendar, 
  Clock, 
  Navigation,
  BarChart3,
  Activity,
  RefreshCw,
  Download,
  X
} from 'lucide-react';

// Use environment variable for API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://sledovanie-aut-api.onrender.com';

// Function to transform direction values
const transformDirection = (direction) => {
  switch (direction) {
    case 'left':
      return 'z Vlárskej';
    case 'right':
      return 'z Na Revíne';
    default:
      return direction;
  }
};

const Dashboard = ({ viewType = 'overview' }) => {
  const [statistics, setStatistics] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dayType, setDayType] = useState('all');
  const [selectedTimeBlock, setSelectedTimeBlock] = useState(null);
  const [timeBlockDetails, setTimeBlockDetails] = useState(null);
  const [loadingBlockDetails, setLoadingBlockDetails] = useState(false);

  const loadStatistics = useCallback(async () => {
    try {
      setLoading(true);
      
      // Pripravenie parametrov pre API volania
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      
      const [basicResponse, detailedResponse, lastUpdateResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/statistics`, { params }),
        axios.get(`${API_BASE_URL}/statistics/detailed`, { params }),
        axios.get(`${API_BASE_URL}/last-update`)
      ]);
      setStatistics(basicResponse.data);
      setDetailedStats(detailedResponse.data);
      setLastUpdate(lastUpdateResponse.data);
    } catch (error) {
      console.error('Chyba pri načítaní štatistík:', error);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  const loadChartData = useCallback(async () => {
    try {
      // Pripravenie parametrov pre charts API
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      params.day_type = dayType;
      
      const chartsResponse = await axios.get(`${API_BASE_URL}/statistics/charts`, { params });
      setChartData(chartsResponse.data);
    } catch (error) {
      console.error('Chyba pri načítaní chart dát:', error);
    }
  }, [fromDate, toDate, dayType]);

  // Jednoduchá funkcia pre aktualizovanie iba chart dát pri zmene dayType
  const updateChartDataOnly = async () => {
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      params.day_type = dayType;
      
      const chartsResponse = await axios.get(`${API_BASE_URL}/statistics/charts`, { params });
      setChartData(chartsResponse.data);
    } catch (error) {
      console.error('Chyba pri načítaní chart dát:', error);
    }
  };

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]); // Samostatný useEffect pre základné štatistiky

  useEffect(() => {
    loadChartData(); // Načítanie charts pri prvom načítaní a pri zmene dátumov
  }, [loadChartData]); // Charts sa načítajú vždy keď sa zmenia dátumové parametre
  
  // Samostatný useEffect pre zmenu dayType - volá iba chart API
  useEffect(() => {
    if (statistics) { // Volaj iba ak sú už načítané základné štatistiky
      updateChartDataOnly();
    }
  }, [dayType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadStatistics(), loadChartData()]);
    setRefreshing(false);
  };

  const handleClearFilters = () => {
    setFromDate('');
    setToDate('');
  };

  const handleTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
  };

  const handleTimeBlockClick = async (hour, timeLabel, count, dayOfWeek = null, dayName = null) => {
    if (count === 0) return; // Neklikať na prázdne bloky
    
    setSelectedTimeBlock({ hour, timeLabel, count, dayOfWeek, dayName });
    setLoadingBlockDetails(true);
    
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      params.day_type = dayType;
      params.hour = hour;
      if (dayOfWeek !== null) params.day_of_week = dayOfWeek;
      
      const response = await axios.get(`${API_BASE_URL}/statistics/time-block-details`, { params });
      setTimeBlockDetails(response.data);
    } catch (error) {
      console.error('Chyba pri načítaní detailov časového bloku:', error);
      setTimeBlockDetails(null);
    } finally {
      setLoadingBlockDetails(false);
    }
  };

  const closeTimeBlockModal = () => {
    setSelectedTimeBlock(null);
    setTimeBlockDetails(null);
  };

  // Check if current filter is set to today
  const isFilteringToday = () => {
    const today = new Date().toISOString().split('T')[0];
    return fromDate === today && toDate === today;
  };

  // Render filter bar component
  const renderFilterBar = () => (
    <div className="flex justify-between items-center mb-6">
      <div className="flex items-center gap-4">
        {/* Date Filter Controls */}
        <div className="date-filter-container">
          <Calendar size={16} className="text-gray-500" />
          <div className="date-filter-group">
            <label className="date-filter-label">Od:</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="date-filter-input"
            />
          </div>
          <div className="date-filter-group">
            <label className="date-filter-label">Do:</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="date-filter-input"
            />
          </div>
          <button 
            className={`btn-today ${isFilteringToday() ? 'active' : ''}`}
            onClick={handleTodayFilter}
            title="Filtrovať dáta z aktuálneho dňa"
          >
            Dnes
          </button>
          {(fromDate || toDate) && (
            <button 
              className="date-filter-clear"
              onClick={handleClearFilters}
            >
              Vymazať filter
            </button>
          )}
        </div>
      </div>
      <button 
        className="btn btn-primary"
        onClick={handleRefresh}
        disabled={refreshing}
      >
        <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
        {refreshing ? 'Obnovujem...' : 'Obnoviť'}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <div className="loading-text">Načítavam štatistiky...</div>
      </div>
    );
  }

  const StatCard = ({ title, value, change, icon: Icon, trend = 'neutral' }) => (
    <div className="stat-card">
      <div className="stat-header">
        <div className="stat-title">{title}</div>
        <Icon className="stat-icon" size={24} />
      </div>
      <div className="stat-value">{value}</div>
      {change && (
        <div className={`stat-change ${trend}`}>
          {trend === 'positive' && <TrendingUp size={16} />}
          {trend === 'negative' && <TrendingDown size={16} />}
          <span>{change}</span>
        </div>
      )}
    </div>
  );

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Main Stats */}
      <div className="stats-grid">
        <StatCard
          title="Celkový počet vozidiel"
          value={statistics.total_records.toLocaleString()}
          icon={Car}
        />
        <StatCard
          title="Priemerný počet za deň"
          value={detailedStats ? Math.round(detailedStats.average_cars_per_day) : 'N/A'}
          icon={Calendar}
        />
        <StatCard
          title="Pracovný týždeň"
          value={detailedStats?.weekday_vs_weekend?.['Týždeň'] ? Math.round(detailedStats.weekday_vs_weekend['Týždeň'].avg_per_day) : 'N/A'}
          change={detailedStats?.weekday_vs_weekend?.['Týždeň'] ? 'pondelok - piatok, denný priemer' : ''}
          icon={Clock}
        />
        <StatCard
          title="Víkend"
          value={detailedStats?.weekday_vs_weekend?.['Víkend'] ? Math.round(detailedStats.weekday_vs_weekend['Víkend'].avg_per_day) : 'N/A'}
          change={detailedStats?.weekday_vs_weekend?.['Víkend'] ? 'sobota - nedeľa, denný priemer' : ''}
          icon={Calendar}
        />
        <StatCard
          title="Najrušnejšia hodina"
          value={detailedStats?.busiest_hours[0] ? `${detailedStats.busiest_hours[0].hour}:00` : 'N/A'}
          change={detailedStats?.busiest_hours[0] ? `${detailedStats.busiest_hours[0].count} vozidiel` : ''}
          icon={Clock}
        />
        <StatCard
          title="Hlavný smer"
          value={detailedStats?.direction_distribution[0]?.direction ? transformDirection(detailedStats.direction_distribution[0].direction) : 'N/A'}
          change={detailedStats?.direction_distribution[0] ? `${detailedStats.direction_distribution[0].count} vozidiel` : ''}
          icon={Navigation}
        />
      </div>

      {/* Hourly Distribution Chart - Full Width */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            <Clock size={20} />
            Rozdelenie podľa hodín
          </div>
          <div className="day-type-controls">
            <button
              className={`day-type-btn ${dayType === 'all' ? 'active' : ''}`}
              onClick={() => setDayType('all')}
            >
              Všetky Dni
            </button>
            <button
              className={`day-type-btn ${dayType === 'weekdays' ? 'active' : ''}`}
              onClick={() => setDayType('weekdays')}
            >
              Pracovné Dni
            </button>
            <button
              className={`day-type-btn ${dayType === 'weekend' ? 'active' : ''}`}
              onClick={() => setDayType('weekend')}
            >
              Víkend
            </button>
          </div>
        </div>
        <div className="card-content">
          <div className="hourly-chart">
            {chartData?.hourly_distribution && (() => {
              const maxCount = Math.max(...chartData.hourly_distribution.map(h => h.count || 0));
              
              return chartData.hourly_distribution.map((block, index) => {
                const percentage = maxCount > 0 ? ((block.count || 0) / maxCount) * 100 : 0;
                const hour = block.hour;
                
                // Zobrazujeme hodiny 0-23
                const timeLabel = `${hour}h`;
                
                return (
                  <div 
                    key={index} 
                    className="hourly-bar"
                    onClick={() => handleTimeBlockClick(hour, timeLabel, block.count || 0)}
                  >
                    <div
                      className="hourly-fill"
                      style={{
                        height: `${percentage}%`,
                        background: `linear-gradient(to top, var(--primary), var(--primary-light))`
                      }}
                      title={`${timeLabel} - ${block.count || 0} vozidiel (klikni pre detaily)`}
                    ></div>
                    <div className="hourly-label">{timeLabel}</div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>

      {/* Charts Section - Two Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Distribution */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <Calendar size={20} />
              Rozdelenie podľa dní
            </div>
          </div>
          <div className="card-content">
            <div className="weekly-chart">
              {detailedStats?.busiest_weekdays.map((day, index) => {
                const maxCount = Math.max(...detailedStats.busiest_weekdays.map(d => d.count));
                const totalWeekdayCount = detailedStats.busiest_weekdays.reduce((sum, d) => sum + d.count, 0);
                const percentage = totalWeekdayCount > 0 ? Math.round((day.count / totalWeekdayCount) * 100) : 0;
                return (
                  <div key={index} className="weekly-item">
                    <div className="weekly-day">{day.day_name}</div>
                    <div className="weekly-bar">
                      <div
                        className="weekly-fill"
                        style={{
                          width: `${(day.count / maxCount) * 100}%`,
                          background: `linear-gradient(to right, var(--accent), #34d399)`
                        }}
                      ></div>
                    </div>
                    <div className="weekly-count">{percentage}%</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Direction Analysis */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <Navigation size={20} />
              Analýza smerov
            </div>
          </div>
          <div className="card-content">
            <div className="direction-grid">
              {detailedStats?.direction_distribution.map((dir, index) => {
                const maxCount = Math.max(...detailedStats.direction_distribution.map(d => d.count));
                const percentage = Math.round((dir.count / statistics.total_records) * 100);
                return (
                  <div key={index} className="direction-card">
                    <div className="direction-name">{transformDirection(dir.direction)}</div>
                    <div className="direction-count">{percentage}%</div>
                    <div className="direction-bar">
                      <div
                        className="direction-fill"
                        style={{
                          width: `${(dir.count / maxCount) * 100}%`,
                          background: `linear-gradient(to right, var(--warning), #fbbf24)`
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Heatmap */}
      {detailedStats?.heatmap_data && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <Activity size={20} />
              Heatmap
            </div>
          </div>
          <div className="card-content">
            <div className="heatmap-container">
              {(() => {
                const maxCount = Math.max(...detailedStats.heatmap_data.map(d => d.count));
                const days = ['Pondelok', 'Utorok', 'Streda', 'Štvrtok', 'Piatok', 'Sobota', 'Nedeľa'];

                return days.map((dayName, dayIndex) => {
                  // Convert frontend index to PostgreSQL DOW: Monday(0) -> 1, Tuesday(1) -> 2, ..., Sunday(6) -> 0
                  const dbDayOfWeek = dayIndex === 6 ? 0 : dayIndex + 1;
                  
                  return (
                    <div key={dayIndex} className="heatmap-row">
                      <div className="heatmap-day-label">{dayName}</div>
                      <div className="heatmap-hours">
                        {Array.from({ length: 24 }, (_, hour) => {
                          const dataPoint = detailedStats.heatmap_data.find(
                            d => d.day_of_week === dbDayOfWeek && d.hour === hour
                          );
                          const intensity = dataPoint ? (dataPoint.count / maxCount) : 0;
                          const count = dataPoint ? dataPoint.count : 0;

                          return (
                            <div
                              key={hour}
                              className="heatmap-cell"
                              style={{
                                backgroundColor: `rgba(99, 102, 241, ${intensity})`,
                                color: intensity > 0.5 ? 'white' : 'var(--gray-600)',
                                cursor: 'pointer'
                              }}
                              title={`${dayName} ${hour}:00 - ${count} vozidiel (klikni pre detaily)`}
                              onClick={() => {
                                const timeLabel = `${hour}h`;
                                handleTimeBlockClick(hour, timeLabel, count, dbDayOfWeek, dayName);
                              }}
                            >
                              {hour}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
    </div>
  );

  const renderReports = () => (
    <div className="space-y-6">
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            <Download size={20} />
            Export údajov
          </div>
          <div className="card-description">
            Exportujte štatistiky a reporty v rôznych formátoch
          </div>
        </div>
        <div className="card-content">
              <div className="export-options">
                <button
                  className="btn btn-outline"
                  onClick={() => {
                    const url = `${API_BASE_URL}/export/csv`;
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'vehicle_records_export.csv');
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  <Download size={16} />
                  Export CSV
                </button>
              </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            <BarChart3 size={20} />
            Súhrnné štatistiky
          </div>
        </div>
        <div className="card-content">
          <div className="summary-grid">
            <div className="summary-item">
              <div className="summary-label">Časové rozpätie</div>
              <div className="summary-value">
                {statistics.date_range.from} - {statistics.date_range.to}
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-label">Celkový počet záznamov</div>
              <div className="summary-value">{statistics.total_records.toLocaleString()}</div>
            </div>
            <div className="summary-item">
              <div className="summary-label">Priemerný denný počet</div>
              <div className="summary-value">
                {detailedStats ? Math.round(detailedStats.average_cars_per_day) : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      {/* Action Bar - always visible */}
      {renderFilterBar()}

      {/* Content based on data availability */}
      {!statistics || statistics.total_records === 0 ? (
        <div className="empty-state">
          <BarChart3 className="empty-icon" size={64} />
          <div className="empty-title">Žiadne údaje</div>
          <div className="empty-description">
            {fromDate || toDate 
              ? 'Pre vybraný dátumový rozsah neboli nájdené žiadne záznamy. Skúste zvoliť iný dátum alebo vymazať filter.'
              : 'Zatiaľ neboli nahrané žiadne CSV súbory s údajmi o vozidlách.'
            }
          </div>
        </div>
      ) : (
        <>
          {/* Content based on view type */}
          {viewType === 'analytics' && renderAnalytics()}
          {viewType === 'reports' && renderReports()}
          {viewType === 'overview' && renderOverview()}
        </>
      )}

      {/* Time Block Details Modal */}
      {selectedTimeBlock && (
        <div className="modal-overlay" onClick={closeTimeBlockModal}>
          <div className="modal-content time-block-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 className="modal-title">
                  {selectedTimeBlock.dayName ? `${selectedTimeBlock.dayName} - ` : ''}
                  Detaily pre {selectedTimeBlock.timeLabel}
                </h3>
                <p className="modal-subtitle">Celkový počet: {selectedTimeBlock.count} vozidiel</p>
              </div>
              <button className="modal-close" onClick={closeTimeBlockModal}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              {loadingBlockDetails ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Načítavam detaily...</p>
                </div>
              ) : timeBlockDetails ? (
                <>
                  {/* Štvrťhodinové rozdelenie */}
                  {timeBlockDetails.quarter_hours && timeBlockDetails.quarter_hours.length > 0 && (
                    <div className="half-hour-breakdown">
                      <h4 className="breakdown-title">
                        <Clock size={18} />
                        Štvrťhodinové rozdelenie
                      </h4>
                      <div className="half-hour-list">
                        {timeBlockDetails.quarter_hours.map((qh, index) => {
                          const displayHour = selectedTimeBlock.hour;
                          const minuteStr = qh.minute.toString().padStart(2, '0');
                          const timeStr = `${displayHour}:${minuteStr}`;
                          return (
                            <div key={index} className="half-hour-item">
                              <span className="half-hour-time">{timeStr}</span>
                              <span className="half-hour-count">{qh.count} vozidiel</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Rozdelenie podľa smerov */}
                  {timeBlockDetails.directions && timeBlockDetails.directions.length > 0 && (
                    <div className="direction-breakdown">
                      <h4 className="breakdown-title">
                        <Navigation size={18} />
                        Rozdelenie podľa smerov
                      </h4>
                      <div className="direction-bars">
                        {timeBlockDetails.directions.map((dir, index) => {
                          const percentage = Math.round((dir.count / selectedTimeBlock.count) * 100);
                          return (
                            <div key={index} className="direction-bar-item">
                              <div className="direction-bar-label">
                                <span className="direction-name">{transformDirection(dir.direction)}</span>
                                <span className="direction-stats">
                                  {dir.count} vozidiel ({percentage}%)
                                </span>
                              </div>
                              <div className="direction-bar-container">
                                <div 
                                  className="direction-bar-fill"
                                  style={{
                                    width: `${percentage}%`,
                                    background: index === 0 
                                      ? 'linear-gradient(to right, var(--primary), var(--primary-light))'
                                      : 'linear-gradient(to right, var(--accent), #34d399)'
                                  }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="error-message">
                  <p>Nepodarilo sa načítať detaily pre tento časový blok.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;